import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp,
  CheckCircle,
  Error,
  Warning,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function SystemAdmin() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [apiStatus, setApiStatus] = useState('checking');
  const [apiData, setApiData] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const username = localStorage.getItem('username');

  const handleLogout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userPermissions');
    navigate('/login');
  }, [navigate]);

  const fetchUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Verify can_view_admin_dash permission
        if (!data.permissions || !data.permissions.includes('can_view_admin_dash')) {
          navigate('/dashboard');
        }
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  }, [navigate, handleLogout]);

  const testApiConnection = useCallback(async () => {
    setApiStatus('checking');
    setTestResult(null);
    
    try {
      const response = await fetch('/api/healthz', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setApiStatus('connected');
        setApiData(data);
        setTestResult({ type: 'success', message: 'Successfully connected to API!' });
      } else {
        setApiStatus('error');
        setTestResult({ 
          type: 'error', 
          message: `API responded with status: ${response.status} ${response.statusText}` 
        });
      }
    } catch (error) {
      setApiStatus('error');
      setTestResult({ 
        type: 'error', 
        message: `Failed to connect to API: ${error.message}` 
      });
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    testApiConnection();
  }, [fetchUserData, testApiConnection]);

  const handleTestEndpoint = async (endpoint, method = 'GET') => {
    setTestResult({ type: 'loading', message: 'Testing endpoint...' });
    
    try {
      const token = localStorage.getItem('accessToken');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      // Add auth token for protected endpoints
      if (!endpoint.includes('health') && !endpoint.includes('version')) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`/api${endpoint}`, {
        method,
        headers,
      });
      
      const data = await response.text();
      let jsonData;
      try {
        jsonData = JSON.parse(data);
      } catch {
        jsonData = data;
      }
      
      setTestResult({
        type: response.ok ? 'success' : 'error',
        message: `${method} ${endpoint}`,
        response: {
          status: response.status,
          statusText: response.statusText,
          data: jsonData
        }
      });
    } catch (error) {
      setTestResult({
        type: 'error',
        message: `Failed to test ${endpoint}`,
        error: error.message
      });
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };


  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      case 'checking':
        return <CircularProgress size={24} />;
      default:
        return <Warning color="warning" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'error':
        return 'error';
      case 'checking':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Box
            component="img"
            sx={{
              height: 40,
              width: 40,
              mr: 2,
            }}
            alt="FiStone Logo"
            src="/fistone-logo.png"
          />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            FiStone System Admin
          </Typography>
          <Chip 
            label="ADMIN" 
            color="warning" 
            size="small" 
            sx={{ mr: 2 }}
          />
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                {username ? username[0].toUpperCase() : <AccountCircle />}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2">{username}</Typography>
              </MenuItem>
              <MenuItem onClick={() => navigate('/dashboard')}>
                User Dashboard
              </MenuItem>
              <MenuItem onClick={() => navigate('/admin/dashboard')}>
                Admin Dashboard
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          System Administration
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🔌 API Connection Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {getStatusIcon(apiStatus)}
                  <Chip 
                    label={
                      apiStatus === 'checking' ? 'Checking API connection...' :
                      apiStatus === 'connected' ? 'Connected to API' :
                      'API connection failed'
                    }
                    color={getStatusColor(apiStatus)}
                  />
                </Box>
                
                <Button 
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={testApiConnection}
                  size="small"
                >
                  Retry Connection
                </Button>

                {apiData && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      API Response:
                    </Typography>
                    <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(apiData, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🧪 API Testing
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Test different API endpoints to verify backend integration:
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid size={6}>
                    <Button 
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestEndpoint('/healthz')}
                      sx={{ mb: 1 }}
                    >
                      GET /api/healthz
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button 
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestEndpoint('/version')}
                      sx={{ mb: 1 }}
                    >
                      GET /api/version
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button 
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestEndpoint('/user')}
                      sx={{ mb: 1 }}
                    >
                      GET /api/user
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button 
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestEndpoint('/goal')}
                      sx={{ mb: 1 }}
                    >
                      GET /api/goal
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button 
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestEndpoint('/associated-person')}
                    >
                      GET /api/associated-person
                    </Button>
                  </Grid>
                  <Grid size={6}>
                    <Button 
                      fullWidth
                      variant="outlined"
                      size="small"
                      onClick={() => handleTestEndpoint('/risk-tolerance')}
                    >
                      GET /api/risk-tolerance
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {testResult && (
            <Grid size={12}>
              <Alert 
                severity={
                  testResult.type === 'success' ? 'success' : 
                  testResult.type === 'error' ? 'error' : 
                  'info'
                }
              >
                <Typography variant="subtitle2">
                  <strong>{testResult.message}</strong>
                </Typography>
                {testResult.response && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Status: {testResult.response.status} {testResult.response.statusText}
                    </Typography>
                    <Paper sx={{ p: 1, mt: 1, bgcolor: 'grey.100' }}>
                      <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                        {JSON.stringify(testResult.response.data, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {testResult.error && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Error: {testResult.error}
                  </Typography>
                )}
              </Alert>
            </Grid>
          )}

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🏗️ Architecture
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="primary">Frontend:</Typography>
                    <Typography variant="body2">React app served from Cloud Storage + CDN</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="primary">Backend:</Typography>
                    <Typography variant="body2">Cloud Run API service (fistone-api)</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="primary">Routing:</Typography>
                    <Typography variant="body2">HTTPS Load Balancer with path-based routing</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="primary">Domain:</Typography>
                    <Typography variant="body2">Custom domain with managed SSL certificate</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📋 Deployment Instructions
                </Typography>
                <ol style={{ marginLeft: '20px', marginTop: '10px' }}>
                  <li>Build and push this web app to Artifact Registry:
                    <Paper sx={{ p: 1, mt: 1, mb: 2, bgcolor: 'grey.100' }}>
                      <code>cd web-app && ./build-and-push.sh v1.0.0</code>
                    </Paper>
                  </li>
                  <li>Deploy using Terraform:
                    <Paper sx={{ p: 1, mt: 1, mb: 2, bgcolor: 'grey.100' }}>
                      <code>terraform apply</code>
                    </Paper>
                  </li>
                  <li>Configure DNS A record for your domain</li>
                  <li>Wait for SSL certificate provisioning (~15 minutes)</li>
                  <li>Your app will be available at your custom domain!</li>
                </ol>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default SystemAdmin;